import React, { useState, useEffect } from 'react';
import { Calendar, LogOut, FileText, Clock, User, Mail, Lock, Home, Download, ChevronRight, DollarSign, Printer } from 'lucide-react';
import { generarPDFHistoriaCompleta, generarPDFSesionIndividual, getProfesionalData } from '../utils/pdfHistoria';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

// ── Paleta ────────────────────────────────────────────────────
const C = {
  rosa:'#F5B8A0', rosaLight:'#FDF0EB', rosaMid:'#F0A88E',
  ocre:'#C4956A', ocreLight:'#F5E8D8', ocreDark:'#A07848',
  teal:'#2D4F4A', tealLight:'#E8F0EF', tealMid:'#3D6560',
  crema:'#FAF7F4', white:'#FFFFFF', gray:'#6B7280', grayLight:'#9CA3AF',
  border:'#E8D5C4', success:'#10B981', warning:'#F59E0B', danger:'#EF4444',
};

const btn = (v='primary', sm=false) => ({
  display:'inline-flex', alignItems:'center', gap:'0.5rem',
  padding: sm ? '0.4rem 1rem' : '0.7rem 1.5rem',
  borderRadius:'12px', border:'none', cursor:'pointer',
  fontSize: sm ? '0.8rem' : '0.95rem', fontWeight:600,
  fontFamily:"'MyriadPro',Arial,sans-serif", whiteSpace:'nowrap',
  transition:'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
  boxShadow:'0 2px 8px rgba(0,0,0,0.1)',
  ...(v==='primary'  && {background:`linear-gradient(135deg,${C.ocre},${C.ocreDark})`,color:'#fff'}),
  ...(v==='teal'     && {background:`linear-gradient(135deg,${C.teal},${C.tealMid})`,color:'#fff'}),
  ...(v==='ghost'    && {background:'transparent',color:C.teal,border:`1.5px solid ${C.border}`,boxShadow:'none'}),
});

const inp = {
  width:'100%',
  padding:'0.8rem 1rem',
  border:`1.5px solid ${C.border}`,
  borderRadius:'12px',
  fontSize:'0.95rem',
  color:C.teal,
  outline:'none',
  fontFamily:"'MyriadPro',Arial,sans-serif",
  background:C.crema,
  boxSizing:'border-box',
  transition:'all 0.25s'
};

const card = {
  background:C.white,
  borderRadius:'16px',
  boxShadow:'0 4px 20px rgba(45,79,74,0.1)',
  border:`1px solid ${C.border}`,
  transition:'all 0.3s ease'
};

export default function PacientePortal() {
  const [paso, setPaso] = useState('login'); // login, portal
  const [paciente, setPaciente] = useState(null);
  const [email, setEmail] = useState('');
  const [cedula, setCedula] = useState('');
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('citas'); // citas, historia, pagos
  const [citas, setCitas] = useState([]);
  const [sesiones, setSesiones] = useState([]);
  const [pagos, setPagos] = useState([]);
  const [citaSeleccionada, setCitaSeleccionada] = useState(null);
  const [psicologa, setPsicologa] = useState(null);

  // Login del paciente
  const hacerLogin = async (e) => {
    e.preventDefault();
    setError('');
    setCargando(true);
    try {
      const res = await fetch(`${API_BASE}/pacientes-login.php?email=${encodeURIComponent(email)}&cedula=${encodeURIComponent(cedula)}`);
      const data = await res.json();
      if (data.error || !data.id) {
        setError(data.error || 'Email o cédula incorrectos');
        setCargando(false);
        return;
      }
      setPaciente(data);
      // Cargar citas y sesiones
      await cargarDatos(data.id);
      setPaso('portal');
    } catch (err) {
      setError('Error al conectar con el servidor');
    }
    setCargando(false);
  };

  const cargarDatos = async (pacienteId) => {
    try {
      const [citasRes, sesionesRes, pagosRes, psicologaRes] = await Promise.all([
        fetch(`${API_BASE}/paciente-citas.php?paciente_id=${pacienteId}`),
        fetch(`${API_BASE}/paciente-sesiones.php?paciente_id=${pacienteId}`),
        fetch(`${API_BASE}/paciente-pagos.php?paciente_id=${pacienteId}`),
        fetch(`${API_BASE}/psicologa-info.php?usuario_id=1`)
      ]);

      const citasData = await citasRes.json();
      const sesionesData = await sesionesRes.json();
      const pagosData = await pagosRes.json();
      const psicologaData = await psicologaRes.json();

      console.log('Citas:', citasData);
      console.log('Sesiones:', sesionesData);
      console.log('Pagos:', pagosData);
      console.log('Psicóloga:', psicologaData);

      setCitas(Array.isArray(citasData) ? citasData : []);
      setSesiones(Array.isArray(sesionesData) ? sesionesData : []);
      setPagos(Array.isArray(pagosData) ? pagosData : []);
      setPsicologa(psicologaData || {});
    } catch (err) {
      console.error('Error cargando datos:', err);
    }
  };

  // Debuguear cuando cambian los datos
  useEffect(() => {
    console.log('Estado actualizado - Citas:', citas, 'Sesiones:', sesiones, 'Pagos:', pagos);
  }, [citas, sesiones, pagos]);

  // Descargar PDF de una sesión específica
  const descargarSesion = async (sesion, indice) => {
    const profesional = await getProfesionalData(sesion.usuario_id, window.location.origin);
    generarPDFSesionIndividual({
      paciente,
      sesion,
      indice,
      profesional,
      baseUrl: window.location.origin
    });
  };


  // Cancelar cita
  const cancelarCita = async (citaId) => {
    if (!window.confirm('¿Estás seguro de que deseas cancelar esta cita?')) return;
    try {
      const res = await fetch(`${API_BASE}/citas.php?id=${citaId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        setCitas(citas.filter(c => c.id !== citaId));
        alert('Cita cancelada exitosamente');
      }
    } catch (err) {
      alert('Error al cancelar la cita');
    }
  };

  // Descargar PDF de historia clínica
  const descargarHistoria = async () => {
    const usuarioId = sesiones[0]?.usuario_id || citas[0]?.usuario_id;
    const profesional = await getProfesionalData(usuarioId, window.location.origin);
    generarPDFHistoriaCompleta({
      paciente,
      sesiones,
      profesional,
      baseUrl: window.location.origin
    });
  };


  // Pantalla de Login
  if (paso === 'login') {
    return (
      <div style={{
        minHeight:'100vh',
        background:`linear-gradient(135deg, ${C.crema} 0%, ${C.tealLight} 100%)`,
        display:'flex',
        alignItems:'center',
        justifyContent:'center',
        padding:'2rem',
        fontFamily:"'MyriadPro',Arial,sans-serif"
      }}>
        <div style={{...card,maxWidth:'450px',width:'100%',padding:'3rem'}}>
          {/* Logo y título */}
          <div style={{textAlign:'center',marginBottom:'2.5rem'}}>
            <img src="/images/logo-horizontal.png" alt="Logo" style={{height:'60px',marginBottom:'1.2rem'}}/>
            <h1 style={{color:C.teal,fontSize:'1.8rem',fontWeight:700,marginBottom:'0.5rem'}}>Mi Portal</h1>
            <p style={{color:C.gray,fontSize:'0.95rem'}}>Accede a tu historia clínica y gestiona tus citas</p>
          </div>

          {/* Formulario */}
          <form onSubmit={hacerLogin} style={{display:'flex',flexDirection:'column',gap:'1.2rem'}}>
            <div>
              <label style={{display:'block',fontWeight:600,color:C.teal,fontSize:'0.85rem',marginBottom:'0.5rem',textTransform:'uppercase'}}>
                <Mail size={16} style={{display:'inline',marginRight:'0.4rem'}}/>
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="tu@email.com"
                required
                style={{...inp,transition:'all 0.3s'}}
                onFocus={(e) => e.target.style.borderColor = C.ocre}
                onBlur={(e) => e.target.style.borderColor = C.border}
              />
            </div>

            <div>
              <label style={{display:'block',fontWeight:600,color:C.teal,fontSize:'0.85rem',marginBottom:'0.5rem',textTransform:'uppercase'}}>
                <Lock size={16} style={{display:'inline',marginRight:'0.4rem'}}/>
                Cédula
              </label>
              <input
                type="text"
                value={cedula}
                onChange={e => setCedula(e.target.value)}
                placeholder="123456789"
                required
                style={{...inp,transition:'all 0.3s'}}
                onFocus={(e) => e.target.style.borderColor = C.ocre}
                onBlur={(e) => e.target.style.borderColor = C.border}
              />
            </div>

            {error && (
              <div style={{background:'#FEE2E2',border:`1px solid #FECACA`,borderRadius:'10px',padding:'0.8rem',color:'#991B1B',fontSize:'0.9rem'}}>
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={cargando}
              style={{...btn('teal'),width:'100%',justifyContent:'center',opacity:cargando?0.6:1}}
            >
              {cargando ? 'Verificando...' : 'Acceder'}
            </button>
          </form>

          {/* Footer */}
          <p style={{textAlign:'center',marginTop:'2rem',color:C.gray,fontSize:'0.85rem'}}>
            ¿Primera vez? Completa el {' '}
            <a href="/formulario" style={{color:C.ocre,fontWeight:600,textDecoration:'none'}}>
              formulario de registro
            </a>
          </p>
        </div>
      </div>
    );
  }

  // Pantalla del Portal
  return (
    <div style={{minHeight:'100vh',background:C.crema,fontFamily:"'MyriadPro',Arial,sans-serif"}}>
      {/* Header */}
      <div style={{background:C.white,borderBottom:`2px solid ${C.border}`,boxShadow:'0 2px 8px rgba(45,79,74,0.08)'}}>
        <div style={{maxWidth:'1200px',margin:'0 auto',padding:'1rem 2rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
          <div style={{display:'flex',alignItems:'center',gap:'1.5rem'}}>
            <img src="/images/logo-horizontal.png" alt="Logo" style={{height:'50px'}}/>
            <div>
              <p style={{color:C.gray,fontSize:'0.8rem',margin:0}}>Bienvenido, {paciente.nombre}</p>
            </div>
          </div>
          <button
            onClick={() => {setPaso('login');setPaciente(null);setEmail('');setCedula('');}}
            style={{...btn('ghost',true)}}
          >
            <LogOut size={14}/> Salir
          </button>
        </div>
      </div>

      <div style={{maxWidth:'1200px',margin:'0 auto',padding:'2rem'}}>
        {/* Tabs */}
        <div style={{display:'flex',gap:'1rem',marginBottom:'2rem',borderBottom:`2px solid ${C.border}`,paddingBottom:'0.5rem'}}>
          <button
            onClick={() => setTab('citas')}
            style={{
              padding:'0.8rem 1.5rem',
              background:'transparent',
              border:'none',
              cursor:'pointer',
              fontWeight:600,
              color:tab==='citas'?C.teal:C.gray,
              fontSize:'0.95rem',
              borderBottom:`3px solid ${tab==='citas'?C.ocre:'transparent'}`,
              transition:'all 0.3s'
            }}
          >
            <Calendar size={16} style={{display:'inline',marginRight:'0.5rem'}}/>
            Mis Citas
          </button>
          <button
            onClick={() => setTab('historia')}
            style={{
              padding:'0.8rem 1.5rem',
              background:'transparent',
              border:'none',
              cursor:'pointer',
              fontWeight:600,
              color:tab==='historia'?C.teal:C.gray,
              fontSize:'0.95rem',
              borderBottom:`3px solid ${tab==='historia'?C.ocre:'transparent'}`,
              transition:'all 0.3s'
            }}
          >
            <FileText size={16} style={{display:'inline',marginRight:'0.5rem'}}/>
            Historia Clínica
          </button>
          <button
            onClick={() => setTab('pagos')}
            style={{
              padding:'0.8rem 1.5rem',
              background:'transparent',
              border:'none',
              cursor:'pointer',
              fontWeight:600,
              color:tab==='pagos'?C.teal:C.gray,
              fontSize:'0.95rem',
              borderBottom:`3px solid ${tab==='pagos'?C.ocre:'transparent'}`,
              transition:'all 0.3s'
            }}
          >
            <DollarSign size={16} style={{display:'inline',marginRight:'0.5rem'}}/>
            Pagos
          </button>
        </div>

        {/* Contenido */}
        {tab === 'citas' && (
          <div>
            <h2 style={{color:C.teal,fontSize:'1.3rem',fontWeight:700,marginBottom:'1.5rem'}}>Mis Citas</h2>
            {citas.length === 0 ? (
              <div style={{...card,padding:'3rem',textAlign:'center',color:C.gray}}>
                <Calendar size={40} style={{margin:'0 auto 1rem',opacity:0.3}}/>
                <p style={{fontWeight:600,marginBottom:'0.5rem'}}>No tienes citas agendadas</p>
                <p style={{fontSize:'0.9rem'}}>Contacta con nosotros para agendar una cita</p>
              </div>
            ) : (
              <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(340px,1fr))',gap:'1.5rem'}}>
                {citas.map(cita => (
                  <div key={cita.id} style={{...card,padding:'1.5rem',overflow:'hidden'}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'1rem'}}>
                      <div>
                        <div style={{color:C.teal,fontWeight:700,fontSize:'0.9rem',textTransform:'uppercase'}}>
                          {cita.estado}
                        </div>
                        <div style={{color:C.gray,fontSize:'0.85rem',marginTop:'0.2rem'}}>
                          {new Date(cita.fecha).toLocaleDateString('es-CO')} • {cita.hora}
                        </div>
                      </div>
                      <div style={{background:C.ocreLight,color:C.ocreDark,padding:'0.4rem 0.8rem',borderRadius:'8px',fontSize:'0.75rem',fontWeight:600}}>
                        {cita.tipo || 'Consulta'}
                      </div>
                    </div>
                    {cita.notas && (
                      <div style={{background:C.crema,padding:'0.8rem',borderRadius:'8px',marginBottom:'1rem',fontSize:'0.85rem',color:C.gray,lineHeight:'1.5'}}>
                        {cita.notas}
                      </div>
                    )}
                    {cita.estado !== 'realizada' && (
                      <button
                        onClick={() => cancelarCita(cita.id)}
                        style={{...btn('danger',true),width:'100%',justifyContent:'center'}}
                      >
                        Cancelar Cita
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'historia' && (
          <div>
            <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'1.5rem'}}>
              <h2 style={{color:C.teal,fontSize:'1.3rem',fontWeight:700}}>Historia Clínica</h2>
              <button
                onClick={descargarHistoria}
                style={{...btn('primary',true)}}
              >
                <Download size={14}/> Descargar PDF
              </button>
            </div>

            {sesiones.length === 0 ? (
              <div style={{...card,padding:'3rem',textAlign:'center',color:C.gray}}>
                <FileText size={40} style={{margin:'0 auto 1rem',opacity:0.3}}/>
                <p style={{fontWeight:600}}>Sin sesiones registradas</p>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:'1.2rem'}}>
                {sesiones.map((sesion, i) => (
                  <div key={sesion.id} style={{...card,padding:'1.5rem',borderLeft:`4px solid ${C.teal}`}}>
                    <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:'1rem'}}>
                      <div style={{flex:1}}>
                        <div style={{fontWeight:700,color:C.teal,fontSize:'0.95rem'}}>Sesión {i+1}</div>
                        <div style={{fontWeight:600,color:C.ocre,fontSize:'0.9rem',marginTop:'0.2rem'}}>{sesion.tema}</div>
                        <div style={{color:C.gray,fontSize:'0.8rem',marginTop:'0.25rem'}}>
                          📅 {new Date(sesion.fecha).toLocaleDateString('es-CO')} • ⏱ {sesion.duracion} min
                        </div>
                      </div>
                      <button
                        onClick={() => descargarSesion(sesion, i)}
                        style={{...btn('primary',true),flexShrink:0,marginLeft:'0.5rem'}}
                      >
                        <Download size={13}/> PDF
                      </button>
                    </div>

                    <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'0.8rem',marginBottom:'1rem',paddingBottom:'1rem',borderBottom:`1px solid ${C.border}33`}}>
                      {sesion.estado_emocional && (
                        <div>
                          <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.2rem'}}>Estado emocional</div>
                          <span style={{background:C.ocreLight,color:C.ocreDark,padding:'0.25rem 0.7rem',borderRadius:8,display:'inline-block',fontSize:'0.76rem',fontWeight:600}}>{sesion.estado_emocional.replace('_',' ')}</span>
                        </div>
                      )}
                      {sesion.diagnosticos && (
                        <div>
                          <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.2rem'}}>Diagnósticos</div>
                          <div style={{color:C.gray,fontSize:'0.78rem'}}>{sesion.diagnosticos.split(' | ').filter(d=>d && d!=='dummy_end').join(', ')}</div>
                        </div>
                      )}
                    </div>

                    {sesion.objetivos_sesion && (
                      <div style={{marginBottom:'0.8rem'}}>
                        <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.2rem'}}>Cuadro clínico</div>
                        <div style={{color:C.gray,fontSize:'0.82rem',lineHeight:'1.5'}}>{sesion.objetivos_sesion}</div>
                      </div>
                    )}

                    {sesion.notas && (
                      <div style={{marginBottom:'0.8rem'}}>
                        <div style={{fontWeight:600,color:C.teal,fontSize:'0.7rem',textTransform:'uppercase',marginBottom:'0.2rem'}}>Notas</div>
                        <div style={{color:C.gray,fontSize:'0.82rem',lineHeight:'1.5'}}>{sesion.notas}</div>
                      </div>
                    )}

                    {sesion.tareas_asignadas && (
                      <div style={{background:'linear-gradient(135deg,#FFFBEB,#FEF3C7)',border:'1.5px solid #FCD34D',borderRadius:8,padding:'0.6rem 0.85rem',color:'#92400E',fontSize:'0.82rem',lineHeight:'1.5'}}>
                        <div style={{fontWeight:700,marginBottom:'0.3rem'}}>Tareas y recomendaciones</div>
                        {sesion.tareas_asignadas}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {tab === 'pagos' && (
          <div>
            <h2 style={{color:C.teal,fontSize:'1.3rem',fontWeight:700,marginBottom:'1.5rem'}}>Mis Pagos</h2>
            {pagos.length === 0 ? (
              <div style={{...card,padding:'3rem',textAlign:'center',color:C.gray}}>
                <DollarSign size={40} style={{margin:'0 auto 1rem',opacity:0.3}}/>
                <p style={{fontWeight:600}}>Sin pagos registrados</p>
              </div>
            ) : (
              <div style={{display:'flex',flexDirection:'column',gap:'1rem'}}>
                {pagos.map(pago => (
                  <div key={pago.id} style={{...card,padding:'1.2rem',display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                    <div>
                      <div style={{fontWeight:600,color:C.teal,fontSize:'0.9rem'}}>
                        {new Date(pago.fecha).toLocaleDateString('es-CO')}
                      </div>
                      <div style={{color:C.gray,fontSize:'0.85rem',marginTop:'0.3rem',textTransform:'capitalize'}}>
                        {pago.metodo}
                      </div>
                      {pago.notas && (
                        <div style={{color:C.gray,fontSize:'0.8rem',marginTop:'0.2rem'}}>{pago.notas}</div>
                      )}
                    </div>
                    <div style={{textAlign:'right'}}>
                      <div style={{fontWeight:700,color:C.success,fontSize:'1.1rem'}}>
                        $ {parseFloat(pago.monto).toLocaleString('es-CO')}
                      </div>
                      <div style={{background:C.ocreLight,color:C.ocreDark,padding:'0.2rem 0.6rem',borderRadius:6,fontSize:'0.7rem',fontWeight:600,marginTop:'0.3rem',display:'inline-block',textTransform:'capitalize'}}>
                        {pago.estado}
                      </div>
                    </div>
                  </div>
                ))}
                {pagos.length > 0 && (
                  <div style={{...card,padding:'1rem',background:C.ocreLight,border:`1px solid ${C.border}`,marginTop:'1rem',textAlign:'right'}}>
                    <span style={{fontWeight:600,color:C.teal}}>Total pagado: </span>
                    <span style={{fontWeight:700,color:C.success,fontSize:'1.1rem',marginLeft:'0.5rem'}}>
                      $ {pagos.reduce((t,p) => t + parseFloat(p.monto), 0).toLocaleString('es-CO')}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
